import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Button
} from '@mui/material';
import { Search, Clear, Refresh, ContentCopy } from '@mui/icons-material';
import { getSocket } from '../../services/realtimeService';
import api from '../../services/api';

interface LogEntry {
  id: number;
  type: 'log' | 'error' | 'warn' | 'info';
  message: string;
  args: string[];
  timestamp: string;
  fullMessage: string;
}

const LogsTab: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Pobierz logi z API
  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { limit: 1000 };
      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await api.get('/logs', { params });
      const fetchedLogs = response.data.logs || [];
      setLogs(fetchedLogs);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd podczas pobierania logów');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtruj logi lokalnie
  useEffect(() => {
    let filtered = [...logs];

    // Filtruj po typie
    if (typeFilter !== 'all') {
      filtered = filtered.filter(log => log.type === typeFilter);
    }

    // Filtruj po wyszukiwaniu
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        log.message.toLowerCase().includes(query) ||
        log.fullMessage.toLowerCase().includes(query) ||
        log.args.some(arg => arg.toLowerCase().includes(query))
      );
    }

    setFilteredLogs(filtered);
  }, [logs, searchQuery, typeFilter]);

  // Auto-scroll do najnowszych logów
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredLogs, autoScroll]);

  // Obsługa scrolla - wyłącz auto-scroll gdy użytkownik scrolluje w górę
  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setAutoScroll(isNearBottom);
    }
  };

  // Funkcja do czyszczenia logów (jak clear w terminalu - tylko czyści widok)
  const clearLogs = () => {
    setLogs([]);
    setFilteredLogs([]);
  };

  // WebSocket - subskrypcja logów w czasie rzeczywistym
  useEffect(() => {
    const socket = getSocket();
    
    const handleNewLog = (logEntry: LogEntry) => {
      setLogs(prev => {
        // Dodaj nowy log na początku (najnowsze na górze)
        const updated = [logEntry, ...prev];
        // Ogranicz do 10000 logów
        return updated.slice(0, 10000);
      });
    };

    const handleHistory = (historyLogs: LogEntry[]) => {
      setLogs(historyLogs);
      setLoading(false);
    };

    const handleError = (errorData: any) => {
      setError(errorData.message || 'Błąd podczas subskrypcji logów');
    };

    const handleCleared = () => {
      setLogs([]);
      setFilteredLogs([]);
    };

    socket.on('log:new', handleNewLog);
    socket.on('logs:history', handleHistory);
    socket.on('logs:error', handleError);
    socket.on('logs:cleared', handleCleared);

    // Subskrybuj logi
    socket.emit('logs:subscribe');

    // Pobierz logi z API przy montowaniu
    fetchLogs();

    return () => {
      socket.off('log:new', handleNewLog);
      socket.off('logs:history', handleHistory);
      socket.off('logs:error', handleError);
      socket.off('logs:cleared', handleCleared);
      socket.emit('logs:unsubscribe');
    };
  }, []);

  const getLogColor = (type: string) => {
    switch (type) {
      case 'error':
        return '#d32f2f';
      case 'warn':
        return '#ed6c02';
      case 'info':
        return '#0288d1';
      default:
        return '#666';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3
      });
    } catch {
      return timestamp;
    }
  };

  const copyLogsToClipboard = async () => {
    try {
      const logsText = filteredLogs.map(log => {
        const timestamp = formatTimestamp(log.timestamp);
        const type = log.type.toUpperCase().padEnd(6);
        const message = log.fullMessage || log.message;
        const argsText = log.args.length > 0 ? `\n  Args: ${log.args.join('\n  ')}` : '';
        return `[${timestamp}] ${type} ${message}${argsText}`;
      }).join('\n\n');

      await navigator.clipboard.writeText(logsText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Błąd podczas kopiowania:', err);
      setError('Nie udało się skopiować logów do schowka');
    }
  };

  return (
    <Box sx={{ px: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
        Logi backendu
      </Typography>

      {/* Filtry */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Szukaj w logach..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 300, flex: 1 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">Wszystkie</MenuItem>
              <MenuItem value="log">Log</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warn">Warning</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant={typeFilter === 'error' ? 'contained' : 'outlined'}
            color="error"
            size="small"
            onClick={() => {
              if (typeFilter === 'error') {
                setTypeFilter('all');
              } else {
                setTypeFilter('error');
              }
            }}
            sx={{ minWidth: 100 }}
          >
            Błędy
            <Chip
              label={logs.filter(log => log.type === 'error').length}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: typeFilter === 'error' ? 'rgba(255, 255, 255, 0.2)' : 'error.main',
                color: 'white'
              }}
            />
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopy />}
            onClick={copyLogsToClipboard}
            disabled={filteredLogs.length === 0}
            title="Kopiuj listę logów do schowka"
          >
            Kopiuj listę
          </Button>

          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<Clear />}
            onClick={clearLogs}
            disabled={logs.length === 0}
            title="Wyczyść wszystkie logi"
          >
            Clear
          </Button>

          <IconButton onClick={fetchLogs} disabled={loading} title="Odśwież">
            <Refresh />
          </IconButton>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {filteredLogs.length} {filteredLogs.length === 1 ? 'log' : 'logów'}
          </Typography>
        </Box>
      </Paper>

      {/* Błąd */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Powiadomienie o sukcesie kopiowania */}
      {copySuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCopySuccess(false)}>
          Lista logów została skopiowana do schowka
        </Alert>
      )}

      {/* Lista logów */}
      <Paper
        sx={{
          p: 2,
          maxHeight: '70vh',
          overflow: 'auto',
          backgroundColor: '#1e1e1e',
          color: '#d4d4d4',
          fontFamily: 'monospace',
          fontSize: '12px'
        }}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {loading && logs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredLogs.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            Brak logów do wyświetlenia
          </Typography>
        ) : (
          <>
            {filteredLogs.map((log) => (
              <Box
                key={log.id}
                sx={{
                  mb: 1,
                  pb: 1,
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                  <Chip
                    label={log.type.toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: getLogColor(log.type),
                      color: '#fff',
                      fontWeight: 'bold',
                      minWidth: 60,
                      fontSize: '10px'
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#888',
                      fontFamily: 'monospace'
                    }}
                  >
                    {formatTimestamp(log.timestamp)}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: getLogColor(log.type),
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {log.fullMessage || log.message}
                </Typography>
                {log.args.length > 0 && (
                  <Box sx={{ mt: 0.5, pl: 2, color: '#999' }}>
                    {log.args.map((arg, idx) => (
                      <Typography key={idx} sx={{ fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                        {arg}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
            <div ref={logsEndRef} />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LogsTab;

